import React, { useState } from 'react'
import { fetchWithCsrf } from '../utils/csrf'
import Modal from './Modal'

interface ComposeMessageProps {
  clubId: number
  memberCount: number
  onClose: () => void
  onSuccess: () => void
}

type MessageType = 'announcement' | 'reminder' | 'custom'

export default function ComposeMessage({ clubId, memberCount, onClose, onSuccess }: ComposeMessageProps) {
  const [messageType, setMessageType] = useState<MessageType>('announcement')
  const [subject, setSubject] = useState('')
  const [content, setContent] = useState('')
  const [customPrompt, setCustomPrompt] = useState('')
  const [isGenerating, setIsGenerating] = useState(false)
  const [isSending, setIsSending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showConfirm, setShowConfirm] = useState(false)

  const handleGenerate = async () => {
    if (messageType === 'custom' && !customPrompt.trim()) {
      setError('Please provide instructions for the custom message')
      return
    }

    setIsGenerating(true)
    setError(null)

    try {
      const response = await fetchWithCsrf(`/api/v1/clubs/${clubId}/messages/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message_type: messageType,
          custom_prompt: messageType === 'custom' ? customPrompt : null
        })
      })

      if (!response.ok) {
        const data = await response.json()
        throw new Error(data.error || 'Failed to generate message')
      }

      const data = await response.json()
      setSubject(data.subject)
      setContent(data.body)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate message')
    } finally {
      setIsGenerating(false)
    }
  }

  const handleSend = async () => {
    if (!subject.trim() || !content.trim()) {
      setError('Subject and message content are required')
      return
    }

    setIsSending(true)
    setError(null)

    try {
      const response = await fetchWithCsrf(`/api/v1/clubs/${clubId}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          subject: subject.trim(),
          content: content.trim(),
          message_type: messageType
        })
      })

      if (!response.ok) {
        const data = await response.json()
        throw new Error(data.error || 'Failed to send message')
      }

      onSuccess()
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send message')
    } finally {
      setIsSending(false)
      setShowConfirm(false)
    }
  }

  return (
    <>
      <Modal isOpen={true} onClose={onClose} size="lg" showCloseButton={true}>
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4">
          <h2 className="text-2xl font-bold text-gray-900">Compose Message</h2>
        </div>

        <div className="p-6 space-y-6">
          {/* Message Type Selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Message Type
            </label>
            <div className="grid grid-cols-3 gap-3">
              <button
                onClick={() => setMessageType('announcement')}
                className={`px-4 py-3 rounded-lg border-2 transition-all ${
                  messageType === 'announcement'
                    ? 'border-indigo-600 bg-indigo-50 text-indigo-700'
                    : 'border-gray-200 hover:border-gray-300 text-gray-700'
                }`}
              >
                <div className="font-semibold">Announcement</div>
                <div className="text-xs mt-1">New book selection</div>
              </button>
              <button
                onClick={() => setMessageType('reminder')}
                className={`px-4 py-3 rounded-lg border-2 transition-all ${
                  messageType === 'reminder'
                    ? 'border-indigo-600 bg-indigo-50 text-indigo-700'
                    : 'border-gray-200 hover:border-gray-300 text-gray-700'
                }`}
              >
                <div className="font-semibold">Reminder</div>
                <div className="text-xs mt-1">Keep reading</div>
              </button>
              <button
                onClick={() => setMessageType('custom')}
                className={`px-4 py-3 rounded-lg border-2 transition-all ${
                  messageType === 'custom'
                    ? 'border-indigo-600 bg-indigo-50 text-indigo-700'
                    : 'border-gray-200 hover:border-gray-300 text-gray-700'
                }`}
              >
                <div className="font-semibold">Custom</div>
                <div className="text-xs mt-1">Your own message</div>
              </button>
            </div>
          </div>

          {/* Custom Prompt (only for custom type) */}
          {messageType === 'custom' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                What would you like to say?
              </label>
              <textarea
                value={customPrompt}
                onChange={(e) => setCustomPrompt(e.target.value)}
                placeholder="E.g., 'Remind everyone about our meeting this Friday at 7pm' or 'Thank everyone for participating in the discussion'"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                rows={3}
              />
            </div>
          )}

          {/* Generate Button */}
          <div className="flex justify-center">
            <button
              onClick={handleGenerate}
              disabled={isGenerating}
              className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold rounded-lg hover:from-indigo-700 hover:to-purple-700 transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isGenerating ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Generating...
                </>
              ) : (
                <>
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  Generate with AI
                </>
              )}
            </button>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {/* Subject */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Subject Line
            </label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Enter subject line..."
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
          </div>

          {/* Content */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Message Content
            </label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Enter your message here... or use AI to generate one!"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent font-mono text-sm"
              rows={12}
            />
            <p className="mt-2 text-sm text-gray-500">
              You can edit the AI-generated message before sending.
            </p>
          </div>

          {/* Recipients Preview */}
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <h3 className="text-sm font-medium text-gray-700 mb-2">Recipients</h3>
            <p className="text-sm text-gray-600">
              This message will be sent to all <strong>{memberCount}</strong> club members.
            </p>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              onClick={onClose}
              className="px-6 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => setShowConfirm(true)}
              disabled={!subject.trim() || !content.trim() || isSending}
              className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
            >
              Send Message
            </button>
          </div>
        </div>
      </Modal>

      {/* Confirmation Dialog */}
      {showConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Send Message?</h3>
            <p className="text-gray-600 mb-6">
              This will send an email to all {memberCount} club members. Are you sure?
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowConfirm(false)}
                disabled={isSending}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSend}
                disabled={isSending}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
              >
                {isSending ? 'Sending...' : 'Yes, Send'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
